/**
 * Chat Error Boundary组件
 *
 * 捕获聊天组件树中的JavaScript错误，记录错误日志，并显示备用UI
 *
 * 功能:
 * - 捕获子组件中的运行时错误
 * - 记录错误信息到控制台
 * - 显示用户友好的错误提示
 * - 提供重试机制
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';

// ==================== 组件接口 ====================

interface ChatErrorBoundaryProps {
  /** 子组件 */
  children: ReactNode;
  /** 自定义错误显示组件(可选) */
  fallback?: ReactNode;
  /** 错误回调函数(可选) */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ChatErrorBoundaryState {
  /** 是否发生错误 */
  hasError: boolean;
  /** 错误信息 */
  error?: Error;
}

// ==================== 组件实现 ====================

/**
 * ChatErrorBoundary组件
 *
 * 使用React类组件实现错误边界功能
 */
export class ChatErrorBoundary extends Component<ChatErrorBoundaryProps, ChatErrorBoundaryState> {
  constructor(props: ChatErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  /**
   * 捕获子组件抛出的错误
   */
  static getDerivedStateFromError(error: Error): ChatErrorBoundaryState {
    // 更新state以在下次渲染时显示备用UI
    return { hasError: true, error };
  }

  /**
   * 记录错误信息
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // 记录错误到控制台
    console.error('[ChatErrorBoundary] 捕获到错误:', error, errorInfo);

    // 调用自定义错误回调
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  /**
   * 重置错误状态，重新尝试渲染
   */
  handleReset = (): void => {
    this.setState({ hasError: false, error: undefined });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // 如果提供了自定义fallback，使用它
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // 默认错误UI
      return (
        <div className="flex flex-col items-center justify-center p-8 bg-white border border-gray-200 rounded-lg min-h-[200px]">
          <div className="text-center">
            {/* 错误图标 */}
            <div className="inline-flex items-center justify-center w-16 h-16 mb-4 bg-red-100 rounded-full">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>

            {/* 错误标题 */}
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              聊天组件出现错误
            </h3>

            {/* 错误信息 */}
            <p className="text-sm text-gray-600 mb-4 max-w-md">
              抱歉，聊天界面遇到了一些问题。请尝试刷新页面或稍后再试。
            </p>

            {/* 错误详情(开发环境) */}
            {import.meta.env.DEV && this.state.error && (
              <details className="mb-4 text-left">
                <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                  查看错误详情
                </summary>
                <pre className="mt-2 p-3 bg-red-50 border border-red-200 rounded text-xs text-red-700 overflow-auto max-h-40">
                  {this.state.error.toString()}
                  {this.state.error.stack}
                </pre>
              </details>
            )}

            {/* 重试按钮 */}
            <button
              onClick={this.handleReset}
              className="inline-flex items-center gap-2 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors"
              aria-label="重试"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span className="text-sm font-medium">重试</span>
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ==================== Hook版本(可选) ====================

/**
 * useChatErrorBoundary Hook
 *
 * 提供函数式组件的错误边界钩子(实验性功能)
 * 注意：这需要React 16.8+和experimental builds
 *
 * @example
 * ```tsx
 * function ChatComponent() {
 *   const [error, resetError] = useChatErrorBoundary();
 *
 *   if (error) {
 *     return <ErrorFallback onReset={resetError} />;
 *   }
 *
 *   return <NormalChat />;
 * }
 * ```
 */
export function useChatErrorBoundary(): [Error | null, () => void] {
  const [error, setError] = React.useState<Error | null>(null);

  const resetError = React.useCallback(() => {
    setError(null);
  }, []);

  // 在实际项目中，这需要配合React的experimental Error Boundary API
  // 这里仅作为接口预留
  return [error, resetError];
}
